import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HelpBadge from './components/HelpBadge';
import CrmTermText, { CrmTermHelp } from './CrmTermText';
import LeadIcpAccordion from './LeadIcpAccordion';
import PursuitExtraPanels from './PursuitExtraPanels';
import { isRealGatewayAccessToken } from './gatewayAuth';
import { isRoleAccessDeniedMessage } from './subscriberNavAccess';
import { pursuitPathForView, pursuitViewFromPath, type PursuitViewId } from './consolePaths';
import { trackUxEvent } from './uxTelemetry';
import { CAMPAIGN_SELECTED_STORAGE_KEY } from './campaignReadiness';
import { type CampaignAudience, type IcpMatrixRow } from './campaignAudience';
import { CONVERSION_TIERS, LEAD_SORTS, SOLUTION_TYPES, asLeadPage, leadQueryPath } from './leadBook';
import './PursuitWorkspace.css';

type PursuitWorkspaceProps = {
  gatewayBaseUrl: string;
  accessToken: string | null;
  language?: 'en' | 'es';
  onSignIn?: () => void;
  onAccessDenied?: () => void;
  /** Stay on the current page. CompliDesk uses this when Vantage is the selected app. */
  embedded?: boolean;
};

type Opportunity = {
  id: number;
  name: string;
  account_id: number;
  account_name?: string | null;
  amount: number;
  stage: string;
  product?: string | null;
  close_date?: string | null;
  forecast_category?: string | null;
  score_pain?: number;
  score_impact?: number;
  score_urgency?: number;
  score_access?: number;
  scorecard_total?: number;
  med_metrics?: number;
  med_economic_buyer?: number;
  med_decision_criteria?: number;
  med_decision_process?: number;
  med_paper_process?: number;
  med_pain?: number;
  med_champion?: number;
  med_competition?: number;
  meddpicc_total?: number;
  champion_contact_id?: number | null;
  economic_buyer_contact_id?: number | null;
  next_action?: string | null;
  next_action_date?: string | null;
  next_action_owner_id?: number | null;
  pain?: string | null;
  impact?: string | null;
  urgency?: string | null;
  owner_id?: number | null;
  lost_reason?: string | null;
  created_at?: string | null;
  age_days?: number | null;
  last_activity_at?: string | null;
};

type PipelineColumn = {
  stage: string;
  count: number;
  amount: number;
  opportunities: Opportunity[];
};

type PipelinePayload = {
  tenant?: string;
  open_amount?: number;
  columns?: PipelineColumn[];
};

type ForecastPayload = {
  pipeline?: number;
  best_case?: number;
  commit?: number;
  closed?: number;
};

type Lead = {
  id: number;
  company_name: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  title?: string | null;
  phone?: string | null;
  notes?: string | null;
  icp_vertical?: string | null;
  status: string;
  source?: string | null;
  tags?: string | null;
  solution_type?: string | null;
  conversion_score?: number;
  proximity_score?: number;
  conversion_tier?: string | null;
  hq_city?: string | null;
  hq_state?: string | null;
  sales_ready?: boolean;
};

type TamAccount = {
  id: number;
  name: string;
  website?: string | null;
  solution_type?: string | null;
  conversion_score?: number;
  proximity_score?: number;
  conversion_tier?: string | null;
  hq_city?: string | null;
  hq_state?: string | null;
  source?: string | null;
};

type LeadStats = {
  sales_ready?: number;
  tam?: number;
  by_solution?: Record<string, number>;
  by_tier?: Record<string, number>;
  by_campaign?: Record<string, number>;
};

type Account = {
  id: number;
  name: string;
  icp_vertical?: string | null;
  industry?: string | null;
  domain?: string | null;
  employee_band?: string | null;
  annual_revenue?: number | null;
};

type Contact = {
  id: number;
  first_name: string;
  last_name: string;
  title?: string | null;
  account_id: number;
  email?: string | null;
};

type Task = {
  id: number;
  title: string;
  due_at?: string | null;
  status: string;
  opportunity_id?: number | null;
  account_id?: number | null;
};

type User = {
  id: number;
  name: string;
};

type WebsiteLead = {
  lead_id: number;
  fullName: string;
  workEmail: string;
  company: string;
  source?: string | null;
  submitted_at?: string;
};

type Activity = {
  id: number;
  kind: string;
  subject: string;
  body?: string | null;
  happened_at?: string;
};

type Project = {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  status?: string;
  currency?: string;
  icp?: string | null;
  website?: string | null;
  protected?: boolean;
};

type Product = {
  id: number;
  name: string;
  sku?: string | null;
  family?: string | null;
  list_price: number;
  billing_period: string;
};

type Campaign = {
  id: number;
  name: string;
  channel: string;
  status: string;
  budget: number;
  notes?: string | null;
  audience?: CampaignAudience | null;
};

type Quote = {
  id: number;
  name: string;
  status: string;
  amount: number;
  opportunity_id: number;
};

type Ticket = {
  id: number;
  subject: string;
  status: string;
  priority: string;
  account_id?: number | null;
};

type Sequence = {
  id: number;
  name: string;
  channel: string;
  steps?: string | null;
  active: boolean;
};

type SequenceEnrollment = {
  id: number;
  sequence_id: number;
  lead_id?: number | null;
  status: string;
};

type ReportPayload = {
  open_amount?: number;
  won_amount?: number;
  win_rate?: number;
  lead_count?: number;
  lead_conversion_rate?: number;
  open_tickets?: number;
  avg_meddpicc?: number;
  overdue_next_action?: number;
  missing_next_action?: number;
  by_stage?: Record<string, { count: number; amount: number }>;
};

type ViewId = PursuitViewId;
type BoardMode = 'kanban' | 'list';
type DealView = 'all' | 'overdue' | 'missing_next' | 'stale';

const STAGES = ['lead', 'qualified', 'solution_fit', 'proposal', 'commit', 'closed_won', 'closed_lost'] as const;
const OPEN_STAGES = new Set(['lead', 'qualified', 'solution_fit', 'proposal', 'commit']);

const PursuitNavIcon: React.FC<{ name: ViewId }> = ({ name }) => {
  const icon = name === 'reports' ? 'insights' : name;
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icon === 'home' ? (
        <>
          <path d="M4 11.2 12 4.5l8 6.7" />
          <path d="M7 10.5V20h10v-9.5" />
        </>
      ) : null}
      {icon === 'leads' ? (
        <>
          <circle cx="9" cy="8.5" r="3" />
          <path d="M4.5 19c.6-3 2.6-4.6 4.5-4.6S13 16 13.6 19" />
          <path d="M16 11h5" />
          <path d="M18.5 8.5v5" />
        </>
      ) : null}
      {icon === 'pipeline' ? (
        <>
          <rect x="3.5" y="5" width="5" height="14" rx="1.2" />
          <rect x="9.5" y="5" width="5" height="14" rx="1.2" />
          <rect x="15.5" y="5" width="5" height="14" rx="1.2" />
        </>
      ) : null}
      {icon === 'tasks' ? (
        <>
          <rect x="4.5" y="4.5" width="15" height="15" rx="2" />
          <path d="M8 12.2 10.4 14.5 16.2 8.8" />
        </>
      ) : null}
      {icon === 'campaigns' ? (
        <>
          <circle cx="12" cy="12" r="7.5" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 4.5v2.2" />
        </>
      ) : null}
      {icon === 'guide' ? <path d="M5 5.5h9.5A3.5 3.5 0 0 1 18 9v10H8.5A3.5 3.5 0 0 0 5 15.5V5.5Z" /> : null}
      {icon === 'accounts' ? (
        <>
          <path d="M4 20V9.5L12 4l8 5.5V20" />
          <path d="M10 20v-6h4v6" />
        </>
      ) : null}
      {icon === 'contacts' ? (
        <>
          <circle cx="12" cy="8.5" r="3" />
          <path d="M5.5 19.2c.8-3.4 3.2-5.2 6.5-5.2s5.7 1.8 6.5 5.2" />
        </>
      ) : null}
      {icon === 'products' ? <path d="M4.5 8 12 4l7.5 4-7.5 4L4.5 8Zm0 0v8L12 20l7.5-4V8" /> : null}
      {icon === 'quotes' ? (
        <>
          <path d="M6 5h12v14H6z" />
          <path d="M9 9h6M9 12h6M9 15h3.5" />
        </>
      ) : null}
      {icon === 'tickets' ? (
        <>
          <path d="M5 8h14v3a2.2 2.2 0 0 0 0 4v3H5v-3a2.2 2.2 0 0 0 0-4V8Z" />
          <path d="M12 8v10" />
        </>
      ) : null}
      {icon === 'sequences' ? <path d="M5 7h6v4H5zM13 13h6v4h-6zM11 9h4M13 13V11" /> : null}
      {icon === 'desk' ? (
        <>
          <path d="M4 18V8h16v10" />
          <path d="M4 12h16" />
          <path d="M8 18v-6M16 18v-6" />
        </>
      ) : null}
      {icon === 'territories' ? (
        <>
          <circle cx="12" cy="11" r="7" />
          <path d="M5 11h14M12 4.2c2.4 2.4 2.4 9.2 0 13.6M12 4.2c-2.4 2.4-2.4 9.2 0 13.6" />
        </>
      ) : null}
      {icon === 'insights' ? (
        <>
          <path d="M5 17V11M10 17V7M15 17v-5M20 17V9" />
        </>
      ) : null}
      {icon === 'projects' ? (
        <>
          <rect x="4" y="5" width="7" height="7" rx="1.2" />
          <rect x="13" y="5" width="7" height="7" rx="1.2" />
          <rect x="4" y="14" width="7" height="5" rx="1.2" />
          <rect x="13" y="14" width="7" height="5" rx="1.2" />
        </>
      ) : null}
      {icon === 'website' ? (
        <>
          <circle cx="12" cy="12" r="8" />
          <path d="M4.5 12h15M12 4.2c2.6 2.6 2.6 12 0 15.6M12 4.2C9.4 6.8 9.4 16.2 12 19.8" />
        </>
      ) : null}
    </svg>
  );
};

const localDatePlusDays = (days: number): string => {
  const when = new Date();
  when.setDate(when.getDate() + days);
  const month = String(when.getMonth() + 1).padStart(2, '0');
  const day = String(when.getDate()).padStart(2, '0');
  return `${when.getFullYear()}-${month}-${day}`;
};

const MED_FIELDS: Array<[keyof Opportunity, string, string]> = [
  ['med_metrics', 'Metrics', 'Métricas'],
  ['med_economic_buyer', 'Economic buyer', 'Comprador económico'],
  ['med_decision_criteria', 'Decision criteria', 'Criterios'],
  ['med_decision_process', 'Decision process', 'Proceso de decisión'],
  ['med_paper_process', 'Paper process', 'Proceso de papel'],
  ['med_pain', 'Identified pain', 'Dolor identificado'],
  ['med_champion', 'Champion', 'Campeón'],
  ['med_competition', 'Competition', 'Competencia'],
];

const SCORE_FIELDS: Array<[keyof Opportunity, string, string]> = [
  ['score_pain', 'Pain', 'Dolor'],
  ['score_impact', 'Impact', 'Impacto'],
  ['score_urgency', 'Urgency', 'Urgencia'],
  ['score_access', 'Access', 'Acceso'],
];

const stageLabel = (stage: string, spanish: boolean): string => {
  const labels: Record<string, [string, string]> = {
    lead: ['Lead', 'Prospecto'],
    qualified: ['Qualified', 'Calificado'],
    solution_fit: ['Solution fit', 'Encaje de solución'],
    proposal: ['Proposal', 'Propuesta'],
    commit: ['Commit', 'Compromiso'],
    closed_won: ['Closed won', 'Ganado'],
    closed_lost: ['Closed lost', 'Perdido'],
  };
  const pair = labels[stage] || [stage, stage];
  return spanish ? pair[1] : pair[0];
};

const money = (value: number | null | undefined): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value || 0));

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const isClosed = (stage: string): boolean => stage === 'closed_won' || stage === 'closed_lost';

const isOverdue = (opp: Opportunity): boolean => {
  if (isClosed(opp.stage) || !opp.next_action_date) return false;
  return opp.next_action_date.slice(0, 10) < todayIso();
};

const missingNextAction = (opp: Opportunity): boolean => OPEN_STAGES.has(opp.stage) && !String(opp.next_action || '').trim();

const isStale = (opp: Opportunity): boolean => missingNextAction(opp) || isOverdue(opp);

const formatError = (err: unknown, spanish: boolean): string => {
  const raw = err instanceof Error ? err.message : String(err);
  if (raw.toLowerCase().includes('missing required rbac')) {
    return spanish
      ? 'Tu rol no incluye CRM. Pide a un administrador crm.pipeline.read / crm.pipeline.write.'
      : 'Your role is missing CRM access. Ask an admin for crm.pipeline.read / crm.pipeline.write.';
  }
  return raw.replace(/^"|"$/g, '');
};

const PursuitWorkspace: React.FC<PursuitWorkspaceProps> = ({
  gatewayBaseUrl,
  accessToken,
  language = 'en',
  onSignIn,
  onAccessDenied,
  embedded = false,
}) => {
  const spanish = language === 'es';
  const hasSession = isRealGatewayAccessToken(accessToken);
  const location = useLocation();
  const navigate = useNavigate();
  const [embeddedView, setEmbeddedView] = React.useState<ViewId>('home');
  const view = embedded ? embeddedView : pursuitViewFromPath(location.pathname);
  const setView = React.useCallback((id: ViewId) => {
    if (embedded) {
      setEmbeddedView(id);
      return;
    }
    navigate(pursuitPathForView(id));
  }, [embedded, navigate]);
  const [project, setProject] = React.useState(() => window.localStorage.getItem('pursuit.project') || '');
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [boardMode, setBoardMode] = React.useState<BoardMode>('kanban');
  const [query, setQuery] = React.useState('');
  const [dealView, setDealView] = React.useState<DealView>('all');
  const [ownerFilter, setOwnerFilter] = React.useState('');
  const [productFilter, setProductFilter] = React.useState('');
  const [pipeline, setPipeline] = React.useState<PipelinePayload | null>(null);
  const [forecast, setForecast] = React.useState<ForecastPayload | null>(null);
  const [leads, setLeads] = React.useState<Lead[]>([]);
  const [accounts, setAccounts] = React.useState<Account[]>([]);
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const [users, setUsers] = React.useState<User[]>([]);
  const [websiteLeads, setWebsiteLeads] = React.useState<WebsiteLead[]>([]);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [campaigns, setCampaigns] = React.useState<Campaign[]>([]);
  const [quotes, setQuotes] = React.useState<Quote[]>([]);
  const [tickets, setTickets] = React.useState<Ticket[]>([]);
  const [sequences, setSequences] = React.useState<Sequence[]>([]);
  const [enrollments, setEnrollments] = React.useState<SequenceEnrollment[]>([]);
  const [reports, setReports] = React.useState<ReportPayload | null>(null);
  const [selected, setSelected] = React.useState<Opportunity | null>(null);
  const [activities, setActivities] = React.useState<Activity[]>([]);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [crmMenuOpen, setCrmMenuOpen] = React.useState(false);
  const [isCompactCrm, setIsCompactCrm] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const media = window.matchMedia('(max-width: 900px)');
    const sync = () => setIsCompactCrm(Boolean(media.matches));
    sync();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', sync);
      return () => media.removeEventListener('change', sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);
  const [crmDataRevision, setCrmDataRevision] = React.useState(0);
  const [leadCampaignId, setLeadCampaignId] = React.useState<number | null>(() => {
    const stored = Number(typeof window === 'undefined' ? 0 : window.localStorage.getItem(CAMPAIGN_SELECTED_STORAGE_KEY) || 0);
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  });
  const [workingLeadId, setWorkingLeadId] = React.useState<number | null>(null);
  const [leadQueue, setLeadQueue] = React.useState<'open' | 'converted' | 'all'>('open');
  const [leadBookTab, setLeadBookTab] = React.useState<'ready' | 'tam'>('ready');
  const [leadStats, setLeadStats] = React.useState<LeadStats>({});
  const [leadPage, setLeadPage] = React.useState<{ items: Lead[]; total: number }>({ items: [], total: 0 });
  const [tamPage, setTamPage] = React.useState<{ items: TamAccount[]; total: number }>({ items: [], total: 0 });
  const [leadSearch, setLeadSearch] = React.useState('');
  const [leadSolution, setLeadSolution] = React.useState('');
  const [leadTier, setLeadTier] = React.useState('');
  const [leadSort, setLeadSort] = React.useState('conversion');
  const [leadOffset, setLeadOffset] = React.useState(0);
  const [selectedLeadIds, setSelectedLeadIds] = React.useState<number[]>([]);
  const leadPageSize = 100;

  const withProject = React.useCallback(
    (path: string) => {
      if (path.startsWith('/api/crm/')) return path;
      if (!project) return path;
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}project=${encodeURIComponent(project)}`;
    },
    [project],
  );

  const callApi = React.useCallback(
    async (path: string, init?: RequestInit) => {
      if (!isRealGatewayAccessToken(accessToken)) {
        throw new Error(
          spanish
            ? 'Inicia sesión con una cuenta de gateway para usar Pursuit.'
            : 'Sign in with a gateway account to use Pursuit.',
        );
      }
      const response = await fetch(`${gatewayBaseUrl}${withProject(path)}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...((init?.headers as Record<string, string> | undefined) || {}),
        },
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { detail?: unknown; message?: string };
        const detail = typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail || payload.message || '');
        throw new Error(detail || `${spanish ? 'Error' : 'Request failed'} (${response.status}).`);
      }
      if (response.status === 204) return null;
      return response.json();
    },
    [accessToken, gatewayBaseUrl, spanish, withProject],
  );

  const refresh = React.useCallback(async () => {
    setError('');
    try {
      const [pipe, fc, ld, ac, ct, tk, us, web, pr, pd, cg, qt, ti, seq, enr, rp, st] = await Promise.all([
        callApi('/api/v1/pursuit/pipeline'),
        callApi('/api/v1/pursuit/forecast'),
        callApi('/api/v1/pursuit/leads'),
        callApi('/api/v1/pursuit/accounts'),
        callApi('/api/v1/pursuit/contacts'),
        callApi('/api/v1/pursuit/tasks'),
        callApi('/api/v1/pursuit/users'),
        callApi('/api/crm/leads?limit=50').catch(() => ({ leads: [] })),
        callApi('/api/v1/pursuit/projects').catch(() => []),
        callApi('/api/v1/pursuit/products').catch(() => []),
        callApi('/api/v1/pursuit/campaigns').catch(() => []),
        callApi('/api/v1/pursuit/quotes').catch(() => []),
        callApi('/api/v1/pursuit/tickets').catch(() => []),
        callApi('/api/v1/pursuit/sequences').catch(() => []),
        callApi('/api/v1/pursuit/enrollments').catch(() => []),
        callApi('/api/v1/pursuit/reports').catch(() => null),
        callApi('/api/v1/pursuit/leads/stats').catch(() => ({})),
      ]);
      setPipeline(pipe);
      setForecast(fc);
      setLeads(Array.isArray(ld) ? ld : []);
      setAccounts(ac);
      setContacts(ct);
      setTasks(tk);
      setUsers(us);
      setWebsiteLeads(Array.isArray(web?.leads) ? web.leads : []);
      setProjects(Array.isArray(pr) ? pr : []);
      setProducts(Array.isArray(pd) ? pd : []);
      setCampaigns(Array.isArray(cg) ? cg : []);
      setQuotes(Array.isArray(qt) ? qt : []);
      setTickets(Array.isArray(ti) ? ti : []);
      setSequences(Array.isArray(seq) ? seq : []);
      setEnrollments(Array.isArray(enr) ? enr : []);
      setReports(rp && typeof rp === 'object' ? rp : null);
      setLeadStats(st && typeof st === 'object' && !Array.isArray(st) ? (st as LeadStats) : {});
      setCrmDataRevision((n) => n + 1);
      const listed = Array.isArray(pr) ? pr : [];
      const active = listed.filter((row: Project) => row.status !== 'archived');
      if (active.length && !active.some((row: Project) => row.slug === project)) {
        const stored = window.localStorage.getItem('pursuit.project') || '';
        const match = active.find((row: Project) => row.slug === stored);
        setProject(match ? match.slug : active[0].slug);
      }
    } catch (err) {
      const message = formatError(err, spanish);
      if (isRoleAccessDeniedMessage(message)) {
        onAccessDenied?.();
        return;
      }
      setError(message);
      trackUxEvent({
        event_type: 'product_error',
        product: 'pursuit',
        last_path: window.location.pathname,
        message,
      });
    } finally {
      setLoading(false);
    }
  }, [callApi, onAccessDenied, spanish]);

  const loadLeadBook = React.useCallback(async () => {
    const query = {
      campaignId: leadCampaignId,
      solutionType: leadSolution,
      conversionTier: leadTier,
      q: leadSearch.trim() || undefined,
      sort: leadSort,
      limit: leadPageSize,
      offset: leadOffset,
    };
    if (leadBookTab === 'tam') {
      const payload = await callApi(leadQueryPath('/api/v1/pursuit/tam-accounts', query));
      const page = asLeadPage(payload);
      setTamPage({ items: page.items as TamAccount[], total: page.total });
      return;
    }
    const payload = await callApi(leadQueryPath('/api/v1/pursuit/leads/query', query));
    const page = asLeadPage(payload, Array.isArray(payload) ? payload : []);
    let items = page.items as Lead[];
    if (leadQueue === 'open') items = items.filter((row) => row.status !== 'converted');
    if (leadQueue === 'converted') items = items.filter((row) => row.status === 'converted');
    setLeadPage({ items, total: page.total });
  }, [callApi, leadBookTab, leadCampaignId, leadOffset, leadQueue, leadSearch, leadSolution, leadSort, leadTier]);

  React.useEffect(() => {
    if (!hasSession || view !== 'leads') return;
    loadLeadBook().catch((err: Error) => setError(formatError(err, spanish)));
  }, [hasSession, view, loadLeadBook, spanish]);

  React.useEffect(() => {
    if (!project) return;
    window.localStorage.setItem('pursuit.project', project);
    setSelected(null);
  }, [project]);

  React.useEffect(() => {
    if (!crmMenuOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCrmMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [crmMenuOpen]);

  React.useEffect(() => {
    if (!hasSession) {
      setLoading(false);
      setError('');
      return;
    }
    refresh().catch((err: Error) => setError(formatError(err, spanish)));
  }, [hasSession, refresh, spanish]);

  React.useEffect(() => {
    if (!selected) {
      setActivities([]);
      return;
    }
    callApi(`/api/v1/pursuit/activities?opportunity_id=${selected.id}`)
      .then((rows) => setActivities(Array.isArray(rows) ? rows : []))
      .catch(() => setActivities([]));
  }, [callApi, selected]);

  const accountName = React.useMemo(() => {
    const map = Object.fromEntries(accounts.map((row) => [row.id, row.name]));
    return (id: number) => map[id] || '—';
  }, [accounts]);

  const allOpportunities = React.useMemo(
    () => (pipeline?.columns || []).flatMap((column) => column.opportunities || []),
    [pipeline],
  );

  const matchesQuery = React.useCallback(
    (opp: Opportunity) => {
      const needle = query.trim().toLowerCase();
      if (!needle) return true;
      return [opp.name, opp.account_name, accountName(opp.account_id), opp.next_action, opp.product, opp.stage]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle);
    },
    [accountName, query],
  );

  const matchesDeal = React.useCallback(
    (opp: Opportunity) => {
      if (!matchesQuery(opp)) return false;
      if (ownerFilter && String(opp.owner_id || '') !== ownerFilter) return false;
      if (productFilter && String(opp.product || '') !== productFilter) return false;
      if (dealView === 'overdue') return isOverdue(opp);
      if (dealView === 'missing_next') return missingNextAction(opp);
      if (dealView === 'stale') return isStale(opp);
      return true;
    },
    [dealView, matchesQuery, ownerFilter, productFilter],
  );

  const visibleOpportunities = React.useMemo(
    () => allOpportunities.filter(matchesDeal),
    [allOpportunities, matchesDeal],
  );

  const clearDealFilters = () => {
    setQuery('');
    setDealView('all');
    setOwnerFilter('');
    setProductFilter('');
  };

  const userName = React.useMemo(() => {
    const map = Object.fromEntries(users.map((row) => [row.id, row.name]));
    return (id?: number | null) => (id ? map[id] || '—' : spanish ? 'Sin asignar' : 'Unassigned');
  }, [spanish, users]);

  const openOpportunities = React.useMemo(
    () => allOpportunities.filter((opp) => OPEN_STAGES.has(opp.stage)),
    [allOpportunities],
  );

  const overdue = openOpportunities.filter(isOverdue);
  const noNext = openOpportunities.filter(missingNextAction);

  const runMutate = async (work: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await work();
      await refresh();
      if (view === 'leads') await loadLeadBook();
    } catch (err) {
      setError(formatError(err, spanish));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const saveOpportunity = async (id: number, body: Record<string, unknown>) => {
    await runMutate(async () => {
      const updated = await callApi(`/api/v1/pursuit/opportunities/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setSelected(updated);
    });
  };

  const convertLead = (leadId: number, productName?: string) =>
    void runMutate(async () => {
      const lead = leads.find((row) => row.id === leadId);
      const plan = (productName || lead?.tags || '').trim();
      const sku = products.find((row) => row.name === plan);
      const converted = (await callApi(`/api/v1/pursuit/leads/${leadId}/convert`, {
        method: 'POST',
        body: JSON.stringify({
          create_opportunity: true,
          product: plan || null,
          amount: sku ? sku.list_price : 0,
          opportunity_name: lead && plan ? `${lead.company_name} — ${plan}` : undefined,
        }),
      })) as { opportunity_id?: number | null; contact_id?: number | null };
      if (converted?.opportunity_id) {
        const talk = String(lead?.notes || lead?.title || 'discovery').slice(0, 220);
        await callApi(`/api/v1/pursuit/opportunities/${converted.opportunity_id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            next_action: `Call ${lead?.first_name || 'buyer'} — ${talk}`,
            next_action_date: localDatePlusDays(2),
            next_action_owner_id: users[0]?.id || null,
          }),
        });
      }
      const sequence = sequences.find((row) => row.active !== false) || sequences[0];
      if (sequence && converted?.contact_id) {
        await callApi(`/api/v1/pursuit/sequences/${sequence.id}/enroll`, {
          method: 'POST',
          body: JSON.stringify({ contact_id: converted.contact_id, lead_id: leadId }),
        });
      }
      setView('pipeline');
    });

  const createFromForm = (path: string, body: Record<string, unknown>, nextView?: ViewId) =>
    void runMutate(async () => {
      await callApi(path, { method: 'POST', body: JSON.stringify(body) });
      if (nextView) setView(nextView);
    });

  const completeTask = (taskId: number) =>
    void runMutate(async () => {
      await callApi(`/api/v1/pursuit/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'done' }),
      });
    });

  const logActivity = (opportunityId: number, subject: string, body?: string) =>
    void runMutate(async () => {
      await callApi('/api/v1/pursuit/activities', {
        method: 'POST',
        body: JSON.stringify({
          kind: 'note',
          subject,
          body: body || null,
          opportunity_id: opportunityId,
        }),
      });
      const rows = await callApi(`/api/v1/pursuit/activities?opportunity_id=${opportunityId}`).catch(() => []);
      setActivities(Array.isArray(rows) ? rows : []);
    });

  const primaryNav: Array<[ViewId, string]> = [
    ['home', spanish ? 'Inicio' : 'Home'],
    ['leads', 'Leads'],
    ['pipeline', 'Pipeline'],
    ['tasks', spanish ? 'Tareas' : 'Tasks'],
  ];
  const setupNav: Array<[ViewId, string]> = [
    ['campaigns', spanish ? 'Campañas' : 'Campaigns'],
    ['guide', spanish ? 'Guía de lanzamiento' : 'Launch guide'],
    ['accounts', spanish ? 'Cuentas' : 'Accounts'],
    ['contacts', spanish ? 'Contactos' : 'Contacts'],
    ['products', spanish ? 'Productos' : 'Products'],
    ['quotes', spanish ? 'Cotizaciones' : 'Quotes'],
    ['tickets', 'Tickets'],
    ['sequences', spanish ? 'Secuencias' : 'Sequences'],
    ['desk', spanish ? 'Mesa de deals' : 'Deal desk'],
    ['territories', spanish ? 'Territorios' : 'Territories'],
    ['insights', 'Insights'],
    ['projects', spanish ? 'Espacios' : 'Workspaces'],
    ['website', spanish ? 'Ingest web' : 'Website'],
  ];
  const dockNav: Array<[ViewId, string]> = [
    ['home', spanish ? 'Inicio' : 'Home'],
    ['tasks', spanish ? 'Tareas' : 'Tasks'],
    ['accounts', spanish ? 'Cuentas' : 'Accounts'],
    ['contacts', spanish ? 'Contactos' : 'Contacts'],
  ];

  const navCount = (id: ViewId): number | null => {
    if (id === 'pipeline') return allOpportunities.length;
    if (id === 'leads') return leads.filter((lead) => lead.status !== 'converted').length;
    if (id === 'accounts') return accounts.length;
    if (id === 'contacts') return contacts.length;
    if (id === 'tasks') return tasks.filter((row) => row.status !== 'done').length;
    if (id === 'tickets') return tickets.filter((row) => row.status !== 'closed' && row.status !== 'done').length;
    return null;
  };

  const renderNav = (items: Array<[ViewId, string]>) =>
    items.map(([id, label]) => {
      const count = navCount(id);
      return (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={view === id}
          className={`pursuit-nav-item${view === id ? ' is-active' : ''}`}
          onClick={() => {
            setView(id);
            setCrmMenuOpen(false);
          }}
        >
          <span className="pursuit-nav-item-label">
            <PursuitNavIcon name={id} />
            {label}
          </span>
          {count !== null ? <em>{count}</em> : null}
        </button>
      );
    });

  const emptyHint = (copy: string, cta: string, onClick: () => void) => (
    <div className="pursuit-empty">
      <div className="pursuit-empty-emoji" aria-hidden="true">✨</div>
      <p>
        <CrmTermText text={copy} spanish={spanish} />
      </p>
      <button type="button" className="dashboard-btn-primary" onClick={onClick}>
        {cta}
      </button>
    </div>
  );

  const activeProject = projects.find((row) => row.slug === project);
  const activeWorkspaces = projects.filter((row) => row.status !== 'archived');
  const showWorkspaceSwitcher = activeWorkspaces.length > 1;
  const openLeads = leads.filter((lead) => lead.status !== 'converted');

  return (
    <section className="dashboard-settings-sections dashboard-settings-sections-wide dashboard-ofac-console" aria-label="Pursuit CRM">
      <article className={`card settings-card dashboard-ofac-shell pursuit-console${isCompactCrm ? ' is-compact' : ''}`}>
      <header className="pursuit-chrome">
        <div className="pursuit-chrome-title">
          {hasSession ? (
            <button
              type="button"
              className="dashboard-btn pursuit-menu-toggle"
              aria-expanded={crmMenuOpen}
              aria-controls="pursuit-crm-menu"
              onClick={() => setCrmMenuOpen((open) => !open)}
            >
              {spanish ? 'Menú CRM' : 'CRM menu'}
            </button>
          ) : null}
          <h2>
            {activeProject?.name || 'Pursuit'}
            <HelpBadge label={spanish ? 'Acerca de este CRM' : 'About this CRM'}>
              <p>
                {spanish
                  ? 'Este es un espacio de ventas (el libro NextCompli). Un lead se convierte en cuenta, contacto y trato. CompliCore, CompliAdvantage y Enterprise son productos del trato, no espacios separados. El tablero muestra cuellos de botella; la lista sirve para comparar. El navegador habla con el gateway, no con Pursuit.'
                  : 'This is one sales workspace (the NextCompli book). A lead becomes an account, a contact, and a deal. CompliCore, CompliAdvantage, and Enterprise are products on the deal — not separate workspaces. Board for bottlenecks, list for scanning. The browser talks to the gateway, not Pursuit.'}
              </p>
            </HelpBadge>
          </h2>
        </div>
        <div className="pursuit-chrome-tools">
          {showWorkspaceSwitcher ? (
            <label className="pursuit-project-switch">
              <span>{spanish ? 'Espacio' : 'Workspace'}</span>
              <select value={project} onChange={(event) => setProject(event.target.value)} disabled={!hasSession}>
                {(activeWorkspaces.length ? activeWorkspaces : [{ slug: project, name: project || '—', id: 0, status: 'active' }]).map(
                  (row) => (
                    <option key={row.slug || 'empty'} value={row.slug}>
                      {row.name}
                    </option>
                  ),
                )}
              </select>
            </label>
          ) : null}
          {showWorkspaceSwitcher ? (
            <button type="button" className="dashboard-btn" onClick={() => setView('projects')}>
              {spanish ? 'Espacios' : 'Workspaces'}
            </button>
          ) : null}
          <button type="button" className="dashboard-btn" onClick={() => void refresh()} disabled={!hasSession || busy || loading}>
            {spanish ? 'Actualizar' : 'Refresh'}
          </button>
          <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => setView('leads')} disabled={!hasSession}>
            {spanish ? 'Nuevo lead' : 'New lead'}
          </button>
        </div>
      </header>

      {!hasSession ? (
        <div className="pursuit-signin" role="status">
          <h3>{spanish ? 'Inicia sesión para abrir el CRM' : 'Sign in to open the CRM'}</h3>
          <p>
            {spanish
              ? 'Este espacio usa tu sesión del gateway. El usuario local de vista previa no envía un token.'
              : 'This workspace uses your gateway session. The local preview user does not send a token.'}
          </p>
          {onSignIn ? (
            <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={onSignIn}>
              {spanish ? 'Iniciar sesión' : 'Sign in'}
            </button>
          ) : (
            <a className="dashboard-btn dashboard-btn-primary" href="/login">
              {spanish ? 'Iniciar sesión' : 'Sign in'}
            </a>
          )}
        </div>
      ) : (
        <>
          {view === 'pipeline' ? (
          <div className="pursuit-kpi-block">
            <div className="pursuit-kpi-heading">
              <h3>{spanish ? 'Pronóstico' : 'Forecast'}</h3>
              <HelpBadge label={spanish ? 'Acerca del pronóstico' : 'About forecast'}>
                <p>
                  {spanish
                    ? 'Cubos de pronóstico. Pipeline abierto = todo lo no cerrado. Commit = lo que defenderá este período. Mejor caso = commit más el alza. Cerrado ganado = ingreso ya cerrado.'
                    : 'Forecast buckets. Open pipeline is every open deal. Commit is what you will defend this period. Best case is commit plus upside. Closed won is booked revenue.'}
                </p>
              </HelpBadge>
            </div>
            <div className="pursuit-kpi-row" role="list" aria-label={spanish ? 'Pronóstico' : 'Forecast'}>
            <button type="button" role="listitem" className="pursuit-kpi" onClick={() => { setView('pipeline'); setDealView('all'); }}>
              <span>{spanish ? 'Pipeline abierto' : 'Open pipeline'}</span>
              <strong>{money(pipeline?.open_amount)}</strong>
            </button>
            <button type="button" role="listitem" className="pursuit-kpi" onClick={() => { setView('pipeline'); setDealView('all'); }}>
              <span>Commit</span>
              <strong>{money(forecast?.commit)}</strong>
            </button>
            <button type="button" role="listitem" className="pursuit-kpi" onClick={() => { setView('pipeline'); setDealView('all'); }}>
              <span>{spanish ? 'Mejor caso' : 'Best case'}</span>
              <strong>{money(forecast?.best_case)}</strong>
            </button>
            <button type="button" role="listitem" className="pursuit-kpi" onClick={() => { setView('pipeline'); setDealView('all'); }}>
              <span>{spanish ? 'Cerrado ganado' : 'Closed won'}</span>
              <strong>{money(forecast?.closed)}</strong>
            </button>
            </div>
          </div>
          ) : null}

          <div className={`pursuit-workspace-grid${selected ? ' has-drawer' : ''}`}>
            {crmMenuOpen ? (
              <>
                <button
                  type="button"
                  className="pursuit-crm-backdrop"
                  aria-label={spanish ? 'Cerrar menú' : 'Close menu'}
                  onClick={() => setCrmMenuOpen(false)}
                />
                <nav
                  id="pursuit-crm-menu"
                  className="pursuit-object-nav pursuit-crm-slideout"
                  aria-label={spanish ? 'Objetos CRM' : 'CRM objects'}
                >
                  <div className="pursuit-crm-slideout-head">
                    <strong>{spanish ? 'Menú CRM' : 'CRM menu'}</strong>
                    <button type="button" className="dashboard-btn" onClick={() => setCrmMenuOpen(false)}>
                      {spanish ? 'Cerrar' : 'Close'}
                    </button>
                  </div>
                  <p>{spanish ? 'Hoy' : 'Daily'}</p>
                  {renderNav(primaryNav)}
                  <p>{spanish ? 'Configurar' : 'Setup'}</p>
                  {renderNav(setupNav)}
                </nav>
              </>
            ) : null}

            <div className="pursuit-stage">
              {view === 'pipeline' ? (
                <div className="pursuit-index-toolbar" role="toolbar" aria-label={spanish ? 'Vista del pipeline' : 'Pipeline view'}>
                  <div className="pursuit-segmented">
                    <button
                      type="button"
                      className={boardMode === 'kanban' ? 'is-active' : undefined}
                      onClick={() => setBoardMode('kanban')}
                    >
                      {spanish ? 'Tablero' : 'Board'}
                    </button>
                    <button
                      type="button"
                      className={boardMode === 'list' ? 'is-active' : undefined}
                      onClick={() => setBoardMode('list')}
                    >
                      {spanish ? 'Lista' : 'List'}
                    </button>
                  </div>
                  <input
                    type="search"
                    className="pursuit-search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={spanish ? 'Buscar tratos…' : 'Search deals…'}
                    aria-label={spanish ? 'Buscar tratos' : 'Search deals'}
                  />
                  <label className="pursuit-owner-filter">
                    <span>{spanish ? 'Propietario' : 'Owner'}</span>
                    <select value={ownerFilter} onChange={(event) => setOwnerFilter(event.target.value)} aria-label={spanish ? 'Filtrar por propietario' : 'Filter by owner'}>
                      <option value="">{spanish ? 'Todos' : 'All owners'}</option>
                      {users.map((row) => (
                        <option key={row.id} value={String(row.id)}>
                          {row.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="pursuit-owner-filter">
                    <span>{spanish ? 'Plan' : 'Plan'}</span>
                    <select value={productFilter} onChange={(event) => setProductFilter(event.target.value)} aria-label={spanish ? 'Filtrar por plan' : 'Filter by plan'}>
                      <option value="">{spanish ? 'Todos los planes' : 'All plans'}</option>
                      {products.map((row) => (
                        <option key={row.id} value={row.name}>
                          {row.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className="pursuit-stage-meta">
                    {visibleOpportunities.length}
                    {visibleOpportunities.length !== allOpportunities.length
                      ? ` / ${allOpportunities.length}`
                      : ''}{' '}
                    {spanish ? 'tratos' : 'deals'}
                  </span>
                </div>
              ) : null}
              {view === 'pipeline' ? (
                <div className="pursuit-view-chips" role="tablist" aria-label={spanish ? 'Vistas guardadas' : 'Saved views'}>
                  {(
                    [
                      ['all', spanish ? 'Todos' : 'All'],
                      ['overdue', spanish ? 'Vencidos' : 'Overdue'],
                      ['missing_next', spanish ? 'Sin siguiente acción' : 'Missing next action'],
                      ['stale', spanish ? 'Estancados' : 'Stale'],
                    ] as Array<[DealView, string]>
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={dealView === id}
                      className={dealView === id ? 'is-active' : undefined}
                      onClick={() => setDealView(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
              {error ? (
                <p className="admin-console-error" role="alert">
                  {error}
                </p>
              ) : null}
              {loading ? <p className="dashboard-panel-subtitle">{spanish ? 'Cargando CRM…' : 'Loading CRM…'}</p> : null}

        {view === 'pipeline' && pipeline && boardMode === 'kanban' ? (
          allOpportunities.length === 0 && openLeads.length === 0 ? (
            <div className="pursuit-empty">
              <p>
                {spanish
                  ? 'Aún no hay tratos. En Inicio elige una campaña, luego crea un lead y conviértelo.'
                  : 'No deals yet. On Home, pick a campaign, then create a lead and convert it.'}
              </p>
              <button type="button" className="dashboard-btn-primary" onClick={() => setView('home')}>
                {spanish ? 'Ir a inicio' : 'Go to Home'}
              </button>
            </div>
          ) : allOpportunities.length === 0 ? (
            <div className="pursuit-empty pursuit-playbook">
              <h3>{spanish ? 'Convierte el lead al pipeline' : 'Convert the lead onto the pipeline'}</h3>
              <p>
                {spanish
                  ? `${openLeads.length} lead${openLeads.length === 1 ? '' : 's'} esperan un plan. Elige CompliCore, CompliAdvantage o Enterprise y convierte.`
                  : `${openLeads.length} lead${openLeads.length === 1 ? '' : 's'} waiting on a plan. Pick CompliCore, CompliAdvantage, or Enterprise, then convert.`}
              </p>
              <button type="button" className="dashboard-btn-primary" onClick={() => setView('leads')}>
                {spanish ? 'Abrir leads' : 'Open leads'}
              </button>
            </div>
          ) : visibleOpportunities.length === 0 ? (
            emptyHint(
              spanish ? 'Ningún trato coincide con esos filtros.' : 'No deals match that search.',
              spanish ? 'Limpiar filtros' : 'Clear search',
              clearDealFilters,
            )
          ) : (
            <div className="pursuit-board">
              {(pipeline.columns || []).map((column) => {
                const cards = (column.opportunities || []).filter(matchesDeal);
                const amount = cards.reduce((sum, opp) => sum + Number(opp.amount || 0), 0);
                return (
                <div key={column.stage} className="pursuit-column" data-stage={column.stage}>
                  <header>
                    <h3>{stageLabel(column.stage, spanish)}</h3>
                    <span>
                      {cards.length} · {money(amount)}
                    </span>
                  </header>
                  {cards.map((opp) => (
                    <button
                      key={opp.id}
                      type="button"
                      className={`pursuit-card${selected?.id === opp.id ? ' is-selected' : ''}${isOverdue(opp) ? ' is-overdue' : ''}${missingNextAction(opp) ? ' is-idle' : ''}`}
                      onClick={() => setSelected(opp)}
                    >
                      <b>{opp.name}</b>
                      <span>{opp.account_name || accountName(opp.account_id)}</span>
                      {opp.product ? <span className="pursuit-product-pill">{opp.product}</span> : null}
                      <strong className="pursuit-card-amount">{money(opp.amount)}</strong>
                      <span className="pursuit-card-meta">
                        {typeof opp.age_days === 'number' ? `${opp.age_days}d` : null}
                        {opp.last_activity_at ? ` · ${String(opp.last_activity_at).slice(0, 10)}` : null}
                      </span>
                      <span className="pursuit-card-signal">
                        {isOverdue(opp)
                          ? spanish
                            ? 'Acción vencida'
                            : 'Overdue'
                          : opp.next_action
                            ? `${opp.next_action}${opp.next_action_date ? ` · ${opp.next_action_date.slice(0, 10)}` : ''}`
                            : spanish
                              ? 'Sin siguiente acción'
                              : 'No next action'}
                      </span>
                    </button>
                  ))}
                </div>
                );
              })}
            </div>
          )
        ) : null}

        {view === 'pipeline' && pipeline && boardMode === 'list' ? (
          <table className="pursuit-table">
            <thead>
              <tr>
                <th>{spanish ? 'Trato' : 'Deal'}</th>
                <th>{spanish ? 'Cuenta' : 'Account'}</th>
                <th>{spanish ? 'Plan' : 'Product'}</th>
                <th>{spanish ? 'Etapa' : 'Stage'}</th>
                <th>{spanish ? 'Monto' : 'Amount'}</th>
                <th>{spanish ? 'Edad' : 'Age'}</th>
                <th>{spanish ? 'Propietario' : 'Owner'}</th>
                <th>{spanish ? 'Siguiente acción' : 'Next action'}</th>
                <th>MEDDPICC</th>
              </tr>
            </thead>
            <tbody>
              {visibleOpportunities.map((opp) => (
                <tr key={opp.id} className={isOverdue(opp) ? 'is-overdue-row' : undefined}>
                  <td>
                    <button type="button" className="pursuit-link" onClick={() => setSelected(opp)}>
                      {opp.name}
                    </button>
                  </td>
                  <td>{opp.account_name || accountName(opp.account_id)}</td>
                  <td>{opp.product || '—'}</td>
                  <td>
                    <span className="pursuit-stage-pill" data-stage={opp.stage}>
                      {stageLabel(opp.stage, spanish)}
                    </span>
                  </td>
                  <td>{money(opp.amount)}</td>
                  <td>{typeof opp.age_days === 'number' ? `${opp.age_days}d` : '—'}</td>
                  <td>{userName(opp.owner_id)}</td>
                  <td>
                    {opp.next_action || (spanish ? 'Ninguna' : 'None')}
                    {opp.next_action_date ? ` · ${opp.next_action_date.slice(0, 10)}` : ''}
                  </td>
                  <td>{opp.meddpicc_total ?? 0}/16</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        {view === 'leads' ? (
          <>
            <LeadIcpAccordion
              spanish={spanish}
              busy={busy}
              campaigns={campaigns}
              selectedId={leadCampaignId}
              onSelect={(id) => {
                setLeadCampaignId(id);
                window.localStorage.setItem(CAMPAIGN_SELECTED_STORAGE_KEY, String(id));
              }}
              onBuildIcp={async (campaignId, description) => {
                const campaign = campaigns.find((row) => row.id === campaignId);
                if (!campaign) return;
                setLeadCampaignId(campaignId);
                window.localStorage.setItem(CAMPAIGN_SELECTED_STORAGE_KEY, String(campaignId));
                let result: {
                  matrix?: IcpMatrixRow[];
                  searches?: Array<{ query?: string; provider?: string; hit_count?: number }>;
                  context_urls?: string[];
                  notes?: string | null;
                } = {};
                await runMutate(async () => {
                  await callApi(`/api/v1/pursuit/campaigns/${campaignId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({
                      audience: {
                        product: campaign.audience?.product || null,
                        description,
                        criteria: [],
                      },
                    }),
                  });
                  result = (await callApi(`/api/v1/pursuit/campaigns/${campaignId}/derive-icp`, {
                    method: 'POST',
                  })) as typeof result;
                });
                return result;
              }}
              onSaveMatrix={async (campaignId, description, matrix) => {
                const campaign = campaigns.find((row) => row.id === campaignId);
                if (!campaign) return;
                await runMutate(async () => {
                  await callApi(`/api/v1/pursuit/campaigns/${campaignId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({
                      audience: {
                        product: campaign.audience?.product || null,
                        description,
                        matrix,
                        criteria: [],
                      },
                    }),
                  });
                });
              }}
              onSearchLeads={async (campaignId, description, matrix, phase, limit) => {
                const campaign = campaigns.find((row) => row.id === campaignId);
                if (!campaign) return;
                let result: {
                  searches?: Array<{ query?: string; provider?: string; hit_count?: number }>;
                  context_urls?: string[];
                  created_count?: number;
                  skipped_existing?: number;
                  total_active?: number;
                  message?: string | null;
                  phase?: string;
                } = {};
                await runMutate(async () => {
                  await callApi(`/api/v1/pursuit/campaigns/${campaignId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({
                      audience: {
                        product: campaign.audience?.product || null,
                        description,
                        matrix,
                        criteria: [],
                      },
                    }),
                  });
                  result = (await callApi(
                    `/api/v1/pursuit/campaigns/${campaignId}/generate-leads?limit=${limit}&phase=${encodeURIComponent(phase)}`,
                    { method: 'POST' },
                  )) as typeof result;
                });
                return result;
              }}
            />
            <form
              className="pursuit-form"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                createFromForm('/api/v1/pursuit/leads', {
                  company_name: String(form.get('company_name') || '').trim(),
                  first_name: String(form.get('first_name') || '').trim(),
                  last_name: String(form.get('last_name') || '').trim(),
                  email: String(form.get('email') || '').trim() || null,
                  title: String(form.get('title') || '').trim() || null,
                  icp_vertical: String(form.get('icp_vertical') || '').trim() || null,
                  tags: String(form.get('product') || '').trim() || null,
                  source: 'manual',
                });
                event.currentTarget.reset();
              }}
            >
              <input name="company_name" placeholder={spanish ? 'Empresa' : 'Company'} required />
              <input name="first_name" placeholder={spanish ? 'Nombre' : 'First'} required />
              <input name="last_name" placeholder={spanish ? 'Apellido' : 'Last'} required />
              <input name="email" type="email" placeholder="Email" />
              <input name="title" placeholder={spanish ? 'Cargo' : 'Title'} />
              <span className="pursuit-field-with-help">
                <input name="icp_vertical" aria-label="ICP" placeholder={spanish ? 'Vertical (opcional)' : 'Vertical (optional)'} />
                <CrmTermHelp term="icp" spanish={spanish} />
              </span>
              <select name="product" aria-label={spanish ? 'Plan' : 'Plan'}>
                <option value="">{spanish ? 'Plan (opcional)' : 'Plan (optional)'}</option>
                {products.map((row) => (
                  <option key={row.id} value={row.name}>
                    {row.name}
                  </option>
                ))}
              </select>
              <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
                {spanish ? 'Añadir lead' : 'Add lead'}
              </button>
            </form>
            {(() => {
              const campaign = campaigns.find((row) => row.id === leadCampaignId) || campaigns[0] || null;
              const visibleLeads = leadPage.items;
              const working = visibleLeads.find((row) => row.id === workingLeadId) || visibleLeads[0] || null;
              const discovery = sequences.find((row) => /discovery/i.test(row.name));
              const bulk = async (action: string, extra: Record<string, unknown> = {}) => {
                await runMutate(async () => {
                  await callApi('/api/v1/pursuit/leads/bulk', {
                    method: 'POST',
                    body: JSON.stringify({ action, ids: selectedLeadIds, ...extra }),
                  });
                  setSelectedLeadIds([]);
                });
              };
              return (
                <>
                  <div className="pursuit-lead-book-toolbar">
                    <label>
                      {spanish ? 'Campaña' : 'Campaign'}
                      <select
                        aria-label={spanish ? 'Filtrar por campaña' : 'Filter by campaign'}
                        value={campaign?.id || ''}
                        onChange={(event) => {
                          const id = Number(event.target.value) || null;
                          setLeadCampaignId(id);
                          setLeadOffset(0);
                          if (id) window.localStorage.setItem(CAMPAIGN_SELECTED_STORAGE_KEY, String(id));
                        }}
                      >
                        <option value="">{spanish ? 'Todas las campañas' : 'All campaigns'}</option>
                        {campaigns.map((row) => (
                          <option key={row.id} value={row.id}>
                            {row.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {spanish ? 'Solución' : 'Solution'}
                      <HelpBadge label={spanish ? 'Tipo de solución' : 'What solution type means'}>
                        {spanish
                          ? 'OFAC, DoD/CMMC, fintech, educación o website. Ordena el almacén TAM y los leads listos para vender.'
                          : 'OFAC, DoD/CMMC, fintech, education, or website. Facets the warehouse and the sales-ready book.'}
                      </HelpBadge>
                      <select
                        aria-label={spanish ? 'Tipo de solución' : 'Solution type'}
                        value={leadSolution}
                        onChange={(event) => {
                          setLeadSolution(event.target.value);
                          setLeadOffset(0);
                        }}
                      >
                        <option value="">{spanish ? 'Todas' : 'All'}</option>
                        {SOLUTION_TYPES.map((row) => (
                          <option key={row} value={row}>
                            {row}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {spanish ? 'Nivel' : 'Tier'}
                      <HelpBadge label={spanish ? 'Qué son los niveles' : 'What tiers mean'}>
                        {spanish
                          ? 'A = alta conversión y cerca de NextCompli. B = outreach en secuencia. C no se envía correo.'
                          : 'A = high conversion and close to NextCompli. B = sequence outreach. C is never emailed.'}
                      </HelpBadge>
                      <select
                        aria-label={spanish ? 'Nivel de conversión' : 'Conversion tier'}
                        value={leadTier}
                        onChange={(event) => {
                          setLeadTier(event.target.value);
                          setLeadOffset(0);
                        }}
                      >
                        <option value="">{spanish ? 'Todos' : 'All'}</option>
                        {CONVERSION_TIERS.map((row) => (
                          <option key={row} value={row}>
                            {row}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {spanish ? 'Ordenar' : 'Sort'}
                      <select
                        aria-label={spanish ? 'Ordenar libro' : 'Sort book'}
                        value={leadSort}
                        onChange={(event) => {
                          setLeadSort(event.target.value);
                          setLeadOffset(0);
                        }}
                      >
                        {LEAD_SORTS.map((row) => (
                          <option key={row.id} value={row.id}>
                            {spanish ? row.es : row.en}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {spanish ? 'Buscar' : 'Search'}
                      <input
                        value={leadSearch}
                        onChange={(event) => {
                          setLeadSearch(event.target.value);
                          setLeadOffset(0);
                        }}
                        placeholder={spanish ? 'Nombre, empresa, email' : 'Name, company, email'}
                      />
                    </label>
                  </div>
                  <div className="pursuit-segmented pursuit-lead-queue" role="group" aria-label={spanish ? 'Cola de leads' : 'Lead queue'}>
                    <button type="button" className={leadBookTab === 'ready' && leadQueue === 'open' ? 'is-active' : undefined} onClick={() => { setLeadBookTab('ready'); setLeadQueue('open'); }}>
                      {spanish ? 'Listos' : 'Sales-ready'} ({leadStats.sales_ready ?? visibleLeads.length})
                    </button>
                    <button type="button" className={leadQueue === 'converted' ? 'is-active' : undefined} onClick={() => { setLeadBookTab('ready'); setLeadQueue('converted'); }}>
                      {spanish ? 'Convertidas' : 'Converted'}
                    </button>
                    <button type="button" className={leadBookTab === 'tam' ? 'is-active' : undefined} onClick={() => setLeadBookTab('tam')}>
                      TAM ({leadStats.tam ?? tamPage.total})
                    </button>
                  </div>
                  {leadBookTab === 'ready' && selectedLeadIds.length ? (
                    <div className="pursuit-lead-bulk">
                      <span>{selectedLeadIds.length} {spanish ? 'seleccionados' : 'selected'}</span>
                      {discovery ? (
                        <button type="button" className="dashboard-security-inline-btn" disabled={busy} onClick={() => void bulk('enroll', { sequence_id: discovery.id })}>
                          {spanish ? 'Inscribir en secuencia' : 'Enroll in sequence'}
                        </button>
                      ) : null}
                      <button type="button" className="dashboard-security-inline-btn" disabled={busy} onClick={() => void bulk('discard')}>
                        {spanish ? 'Quitar' : 'Remove'}
                      </button>
                    </div>
                  ) : null}
                  {leadBookTab === 'tam' ? (
                    <>
                      <p className="pursuit-icp-hint">
                        {spanish
                          ? 'Almacén de empresas reales (USAspending / IPEDS). No hay nombres inventados. Open solo muestra personas con email.'
                          : 'Company warehouse from USAspending and IPEDS. No invented names. Open only shows people with emails.'}
                      </p>
                      <div className="pursuit-lead-bulk">
                        <button
                          type="button"
                          className="dashboard-security-inline-btn"
                          disabled={busy}
                          onClick={() =>
                            void runMutate(async () => {
                              await callApi('/api/v1/pursuit/jobs/tam-ingest', {
                                method: 'POST',
                                body: JSON.stringify({ vertical: leadSolution || 'dod', limit: 500, campaign_id: leadCampaignId }),
                              });
                            })
                          }
                        >
                          {spanish ? 'Cargar TAM público' : 'Load public TAM'}
                        </button>
                      </div>
                      {tamPage.items.length === 0 ? (
                        emptyHint(
                          spanish
                            ? 'El almacén TAM está vacío. Cargue DoD o educación, o busque empresas en una campaña outbound.'
                            : 'TAM warehouse is empty. Load DoD or education, or search companies from an outbound campaign.',
                          spanish ? 'Pipeline' : 'Back to pipeline',
                          () => setView('pipeline'),
                        )
                      ) : (
                        <>
                          <table className="pursuit-table">
                            <thead>
                              <tr>
                                <th>{spanish ? 'Empresa' : 'Company'}</th>
                                <th>{spanish ? 'Solución' : 'Solution'}</th>
                                <th>{spanish ? 'Nivel' : 'Tier'}</th>
                                <th>{spanish ? 'Conversión' : 'Conversion'}</th>
                                <th>{spanish ? 'Proximidad' : 'Proximity'}</th>
                                <th>{spanish ? 'Lugar' : 'Place'}</th>
                                <th>{spanish ? 'Fuente' : 'Source'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tamPage.items.map((row) => (
                                <tr key={row.id}>
                                  <td>{row.name}</td>
                                  <td>{row.solution_type || '—'}</td>
                                  <td>{row.conversion_tier || '—'}</td>
                                  <td>{row.conversion_score ?? '—'}</td>
                                  <td>{row.proximity_score ?? '—'}</td>
                                  <td>{[row.hq_city, row.hq_state].filter(Boolean).join(', ') || '—'}</td>
                                  <td>{row.source || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="pursuit-icp-hint">
                            {tamPage.total} {spanish ? 'empresas' : 'companies'} · {leadOffset + 1}–{Math.min(leadOffset + leadPageSize, tamPage.total)}
                          </p>
                        </>
                      )}
                    </>
                  ) : visibleLeads.length === 0 ? (
                    emptyHint(
                      spanish
                        ? 'No hay leads listos para vender en este filtro. El TAM puede tener empresas; Open nunca muestra Buyer Team ni emails vacíos.'
                        : 'No sales-ready leads in this filter. Add one by hand, load the TAM warehouse, or wait for website ingest. Open never shows Buyer Team or empty emails.',
                      spanish ? 'Pipeline' : 'Back to pipeline',
                      () => setView('pipeline'),
                    )
                  ) : (
                    <>
                      <table className="pursuit-table">
                        <thead>
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                aria-label={spanish ? 'Seleccionar página' : 'Select page'}
                                checked={visibleLeads.length > 0 && selectedLeadIds.length === visibleLeads.length}
                                onChange={(event) => setSelectedLeadIds(event.target.checked ? visibleLeads.map((row) => row.id) : [])}
                              />
                            </th>
                            <th>{spanish ? 'Empresa' : 'Company'}</th>
                            <th>{spanish ? 'Contacto' : 'Contact'}</th>
                            <th>Email</th>
                            <th>{spanish ? 'Cargo' : 'Title'}</th>
                            <th>{spanish ? 'Solución' : 'Solution'}</th>
                            <th>{spanish ? 'Nivel' : 'Tier'}</th>
                            <th>{spanish ? 'Prox.' : 'Prox.'}</th>
                            <th>Status</th>
                            <th>{spanish ? 'Plan' : 'Plan'}</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {visibleLeads.map((lead) => (
                            <tr key={lead.id} className={workingLeadId === lead.id ? 'is-working-lead' : undefined}>
                              <td>
                                <input
                                  type="checkbox"
                                  aria-label={lead.company_name}
                                  checked={selectedLeadIds.includes(lead.id)}
                                  onChange={(event) => {
                                    setSelectedLeadIds((ids) =>
                                      event.target.checked ? [...ids, lead.id] : ids.filter((id) => id !== lead.id),
                                    );
                                  }}
                                />
                              </td>
                              <td>
                                <button type="button" className="pursuit-link" onClick={() => setWorkingLeadId(lead.id)}>
                                  {lead.company_name}
                                </button>
                              </td>
                              <td>{`${lead.first_name} ${lead.last_name}`}</td>
                              <td>{lead.email || '—'}</td>
                              <td>{lead.title || '—'}</td>
                              <td>{lead.solution_type || '—'}</td>
                              <td>{lead.conversion_tier || '—'}</td>
                              <td>{lead.proximity_score ?? '—'}</td>
                              <td>{lead.status}</td>
                              <td>{lead.tags || '—'}</td>
                              <td>
                                {lead.status !== 'converted' ? (
                                  <LeadConvertControls
                                    lead={lead}
                                    products={products}
                                    busy={busy}
                                    spanish={spanish}
                                    onConvert={convertLead}
                                  />
                                ) : null}
                                <button
                                  type="button"
                                  className="dashboard-security-inline-btn"
                                  disabled={busy}
                                  onClick={() => void runMutate(async () => { await callApi(`/api/v1/pursuit/leads/${lead.id}`, { method: 'DELETE' }); })}
                                >
                                  {spanish ? 'Quitar' : 'Remove'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="pursuit-lead-pager">
                        <button type="button" className="dashboard-security-inline-btn" disabled={leadOffset <= 0} onClick={() => setLeadOffset(Math.max(0, leadOffset - leadPageSize))}>
                          {spanish ? 'Anterior' : 'Previous'}
                        </button>
                        <span>
                          {leadPage.total} {spanish ? 'listos' : 'sales-ready'} · {leadOffset + 1}–{Math.min(leadOffset + leadPageSize, leadPage.total)}
                        </span>
                        <button type="button" className="dashboard-security-inline-btn" disabled={leadOffset + leadPageSize >= leadPage.total} onClick={() => setLeadOffset(leadOffset + leadPageSize)}>
                          {spanish ? 'Siguiente' : 'Next'}
                        </button>
                      </div>
                      {working ? (
                        <aside className="pursuit-lead-workbench" aria-label={spanish ? 'Trabajar este lead' : 'Work this lead'}>
                          <h4>{working.company_name}</h4>
                          <dl>
                            <div>
                              <dt>{spanish ? 'Contacto' : 'Contact'}</dt>
                              <dd>
                                {`${working.first_name} ${working.last_name}`}
                                {working.title ? ` · ${working.title}` : ''}
                              </dd>
                            </div>
                            <div>
                              <dt>Email</dt>
                              <dd>{working.email || (spanish ? 'Sin email todavía' : 'No email yet')}</dd>
                            </div>
                            <div>
                              <dt>{spanish ? 'Nivel / proximidad' : 'Tier / proximity'}</dt>
                              <dd>
                                {working.conversion_tier || '—'} · {working.conversion_score ?? '—'} / {working.proximity_score ?? '—'}
                                {working.hq_state ? ` · ${[working.hq_city, working.hq_state].filter(Boolean).join(', ')}` : ''}
                              </dd>
                            </div>
                            <div>
                              <dt>{spanish ? 'Biografía / ICP' : 'Bio / ICP'}</dt>
                              <dd>{working.icp_vertical || '—'} · {spanish ? 'Plan' : 'Plan'} {working.tags || '—'}</dd>
                            </div>
                            <div>
                              <dt>{spanish ? 'Qué decir' : 'Talk plan'}</dt>
                              <dd>{working.notes || (spanish ? 'Añada notas: dolor, opener y CTA.' : 'Add notes: pain, opener, and CTA.')}</dd>
                            </div>
                          </dl>
                        </aside>
                      ) : null}
                    </>
                  )}
                </>
              );
            })()}
          </>
        ) : null}

        {view === 'accounts' ? (
          <>
            <form
              className="pursuit-form"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                createFromForm('/api/v1/pursuit/accounts', {
                  name: String(form.get('name') || '').trim(),
                  domain: String(form.get('domain') || '').trim() || null,
                  industry: String(form.get('industry') || '').trim() || null,
                  icp_vertical: String(form.get('icp_vertical') || '').trim() || null,
                  employee_band: String(form.get('employee_band') || '').trim() || null,
                  annual_revenue: Number(form.get('annual_revenue')) || null,
                });
                event.currentTarget.reset();
              }}
            >
              <input name="name" placeholder={spanish ? 'Nombre de cuenta' : 'Account name'} required />
              <input name="domain" placeholder="Domain" />
              <input name="industry" placeholder={spanish ? 'Industria' : 'Industry'} />
              <span className="pursuit-field-with-help">
                <input name="icp_vertical" aria-label="ICP" placeholder={spanish ? 'Vertical (opcional)' : 'Vertical (optional)'} />
                <CrmTermHelp term="icp" spanish={spanish} />
              </span>
              <select name="employee_band" defaultValue="" aria-label={spanish ? 'Empleados' : 'Employees'}>
                <option value="">{spanish ? 'Empleados' : 'Employees'}</option>
                <option value="1-10">1–10</option>
                <option value="11-50">11–50</option>
                <option value="51-200">51–200</option>
                <option value="201-500">201–500</option>
                <option value="501-1000">501–1000</option>
                <option value="1000+">1000+</option>
              </select>
              <input name="annual_revenue" type="number" min="0" step="1000" placeholder={spanish ? 'Ingresos anuales' : 'Annual revenue'} />
              <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
                {spanish ? 'Añadir cuenta' : 'Add account'}
              </button>
            </form>
            <table className="pursuit-table">
              <thead>
                <tr>
                  <th>{spanish ? 'Nombre' : 'Name'}</th>
                  <th>ICP</th>
                  <th>{spanish ? 'Industria' : 'Industry'}</th>
                  <th>{spanish ? 'Empleados' : 'Employees'}</th>
                  <th>{spanish ? 'Ingresos' : 'Revenue'}</th>
                  <th>Domain</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.icp_vertical || '—'}</td>
                    <td>{row.industry || '—'}</td>
                    <td>{row.employee_band || '—'}</td>
                    <td>{row.annual_revenue ? money(row.annual_revenue) : '—'}</td>
                    <td>{row.domain || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}

        {view === 'contacts' ? (
          <>
            <form
              className="pursuit-form"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                createFromForm('/api/v1/pursuit/contacts', {
                  account_id: Number(form.get('account_id')),
                  first_name: String(form.get('first_name') || '').trim(),
                  last_name: String(form.get('last_name') || '').trim(),
                  title: String(form.get('title') || '').trim() || null,
                  email: String(form.get('email') || '').trim() || null,
                });
                event.currentTarget.reset();
              }}
            >
              <select name="account_id" required defaultValue="">
                <option value="" disabled>
                  {spanish ? 'Cuenta' : 'Account'}
                </option>
                {accounts.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
              <input name="first_name" placeholder={spanish ? 'Nombre' : 'First'} required />
              <input name="last_name" placeholder={spanish ? 'Apellido' : 'Last'} required />
              <input name="title" placeholder={spanish ? 'Cargo' : 'Title'} />
              <input name="email" type="email" placeholder="Email" />
              <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
                {spanish ? 'Añadir contacto' : 'Add contact'}
              </button>
            </form>
            <table className="pursuit-table">
              <thead>
                <tr>
                  <th>{spanish ? 'Nombre' : 'Name'}</th>
                  <th>{spanish ? 'Cargo' : 'Title'}</th>
                  <th>{spanish ? 'Cuenta' : 'Account'}</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((row) => (
                  <tr key={row.id}>
                    <td>{`${row.first_name} ${row.last_name}`}</td>
                    <td>{row.title || '—'}</td>
                    <td>{accountName(row.account_id)}</td>
                    <td>{row.email || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}

        {view === 'tasks' ? (
          <>
            <form
              className="pursuit-form"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                const accountId = Number(form.get('account_id'));
                const opportunityId = Number(form.get('opportunity_id'));
                createFromForm('/api/v1/pursuit/tasks', {
                  title: String(form.get('title') || '').trim(),
                  due_at: String(form.get('due_at') || '').trim() || null,
                  account_id: accountId || null,
                  opportunity_id: opportunityId || null,
                });
                event.currentTarget.reset();
              }}
            >
              <input name="title" placeholder={spanish ? 'Siguiente acción' : 'Next action'} required />
              <input name="due_at" type="date" />
              <select name="account_id" defaultValue="">
                <option value="">{spanish ? 'Cuenta (opcional)' : 'Account (optional)'}</option>
                {accounts.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
              <select name="opportunity_id" defaultValue="">
                <option value="">{spanish ? 'Trato (opcional)' : 'Deal (optional)'}</option>
                {allOpportunities.map((opp) => (
                  <option key={opp.id} value={opp.id}>
                    {opp.name}
                  </option>
                ))}
              </select>
              <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
                {spanish ? 'Añadir tarea' : 'Add task'}
              </button>
            </form>
            <table className="pursuit-table">
              <thead>
                <tr>
                  <th>{spanish ? 'Tarea' : 'Task'}</th>
                  <th>{spanish ? 'Vence' : 'Due'}</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tasks.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title}</td>
                    <td>{row.due_at || '—'}</td>
                    <td>{row.status}</td>
                    <td>
                      {row.status !== 'done' ? (
                        <button type="button" className="dashboard-security-inline-btn" disabled={busy} onClick={() => completeTask(row.id)}>
                          {spanish ? 'Hecho' : 'Done'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}

        <PursuitExtraPanels
          view={view}
          spanish={spanish}
          busy={busy}
          project={project}
          projects={projects}
          products={products}
          campaigns={campaigns}
          leads={leads}
          quotes={quotes}
          tickets={tickets}
          sequences={sequences}
          enrollments={enrollments}
          reports={reports}
          accounts={accounts}
          opportunities={allOpportunities}
          pipelineStages={(pipeline?.columns || []).map((column) => ({
            stage: column.stage,
            count: column.count,
            amount: column.amount,
          }))}
          overdueCount={overdue.length}
          missingNextCount={noNext.length}
          openLeadCount={openLeads.length}
          openDealCount={openOpportunities.length}
          openAmount={Number(pipeline?.open_amount || 0)}
          openTaskCount={tasks.filter((row) => row.status !== 'done').length}
          websiteLeadCount={websiteLeads.length}
          enrollmentCount={enrollments.length}
          money={money}
          accountName={accountName}
          setProject={setProject}
          setView={setView}
          onOpenPipeline={(dealView) => {
            if (dealView === 'overdue' || dealView === 'missing_next' || dealView === 'stale' || dealView === 'all') {
              setDealView(dealView);
            }
            setView('pipeline');
          }}
          createFromForm={createFromForm}
          runMutate={runMutate}
          callApi={callApi}
          crmDataRevision={crmDataRevision}
          leadStats={leadStats}
        />

        {view === 'website' ? (
          websiteLeads.length === 0 ? (
            emptyHint(
              spanish
                ? 'El formulario público escribe en el gateway y dual-write a Pursuit. Aún no hay ingest.'
                : 'The public form writes to the gateway and dual-writes into Pursuit. Nothing ingested yet.',
              spanish ? 'Ver leads' : 'Open leads',
              () => setView('leads'),
            )
          ) : (
            <table className="pursuit-table">
              <thead>
                <tr>
                  <th>{spanish ? 'Nombre' : 'Name'}</th>
                  <th>{spanish ? 'Empresa' : 'Company'}</th>
                  <th>Email</th>
                  <th>{spanish ? 'Fuente' : 'Source'}</th>
                </tr>
              </thead>
              <tbody>
                {websiteLeads.map((lead) => (
                  <tr key={lead.lead_id}>
                    <td>{lead.fullName}</td>
                    <td>{lead.company}</td>
                    <td>{lead.workEmail}</td>
                    <td>{lead.source || 'website'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : null}
            </div>

            {selected ? (
              <OpportunityDrawer
                opp={selected}
                users={users}
                contacts={contacts.filter((row) => row.account_id === selected.account_id)}
                products={products}
                activities={activities}
                busy={busy}
                spanish={spanish}
                onClose={() => setSelected(null)}
                onSave={saveOpportunity}
                onLogActivity={logActivity}
              />
            ) : null}
          </div>
        </>
      )}
      {hasSession && isCompactCrm ? (
        <nav className="pursuit-dock" aria-label={spanish ? 'Accesos diarios' : 'Daily CRM'}>
          {renderNav(dockNav)}
          <button
            type="button"
            className="pursuit-nav-item"
            aria-label={spanish ? 'Más objetos CRM' : 'More CRM objects'}
            onClick={() => setCrmMenuOpen(true)}
          >
            <span className="pursuit-nav-item-label">
              {spanish ? 'Más' : 'More'}
            </span>
          </button>
        </nav>
      ) : null}
      </article>
    </section>
  );
};

const LeadConvertControls: React.FC<{
  lead: Lead;
  products: Product[];
  busy: boolean;
  spanish: boolean;
  onConvert: (leadId: number, productName?: string) => void;
}> = ({ lead, products, busy, spanish, onConvert }) => {
  const [plan, setPlan] = React.useState(lead.tags || products[0]?.name || '');
  return (
    <span className="pursuit-convert-cell">
      <select
        value={plan}
        aria-label={spanish ? `Plan para ${lead.company_name}` : `Plan for ${lead.company_name}`}
        onChange={(event) => setPlan(event.target.value)}
      >
        {products.length === 0 ? <option value="">{spanish ? 'Sin catálogo' : 'No catalog'}</option> : null}
        {products.map((row) => (
          <option key={row.id} value={row.name}>
            {row.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="dashboard-security-inline-btn"
        disabled={busy}
        onClick={() => onConvert(lead.id, plan)}
      >
        {spanish ? 'Convertir' : 'Convert'}
      </button>
    </span>
  );
};

type DrawerProps = {
  opp: Opportunity;
  users: User[];
  contacts: Contact[];
  products: Product[];
  activities: Activity[];
  busy: boolean;
  spanish: boolean;
  onClose: () => void;
  onSave: (id: number, body: Record<string, unknown>) => Promise<void>;
  onLogActivity: (opportunityId: number, subject: string, body?: string) => void;
};

const OpportunityDrawer: React.FC<DrawerProps> = ({
  opp,
  users,
  contacts,
  products,
  activities,
  busy,
  spanish,
  onClose,
  onSave,
  onLogActivity,
}) => {
  const [draft, setDraft] = React.useState<Opportunity>(opp);
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    setDraft(opp);
    setNote('');
  }, [opp]);

  const setField = (key: keyof Opportunity, value: string | number | null) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const submit = () =>
    void onSave(opp.id, {
      stage: draft.stage,
      product: draft.product || null,
      amount: Number(draft.amount || 0),
      next_action: draft.next_action || null,
      next_action_date: draft.next_action_date || null,
      next_action_owner_id: draft.next_action_owner_id || null,
      pain: draft.pain || null,
      impact: draft.impact || null,
      urgency: draft.urgency || null,
      score_pain: Number(draft.score_pain || 0),
      score_impact: Number(draft.score_impact || 0),
      score_urgency: Number(draft.score_urgency || 0),
      score_access: Number(draft.score_access || 0),
      med_metrics: Number(draft.med_metrics || 0),
      med_economic_buyer: Number(draft.med_economic_buyer || 0),
      med_decision_criteria: Number(draft.med_decision_criteria || 0),
      med_decision_process: Number(draft.med_decision_process || 0),
      med_paper_process: Number(draft.med_paper_process || 0),
      med_pain: Number(draft.med_pain || 0),
      med_champion: Number(draft.med_champion || 0),
      med_competition: Number(draft.med_competition || 0),
      champion_contact_id: draft.champion_contact_id || null,
      economic_buyer_contact_id: draft.economic_buyer_contact_id || null,
      lost_reason: draft.stage === 'closed_lost' ? draft.lost_reason || null : null,
    });

  return (
    <aside className="pursuit-drawer" aria-label={opp.name}>
      <header>
        <div>
          <h3>{opp.name}</h3>
          <p>
            {money(opp.amount)} · {stageLabel(opp.stage, spanish)}
          </p>
        </div>
        <button type="button" className="dashboard-security-inline-btn" onClick={onClose}>
          {spanish ? 'Cerrar' : 'Close'}
        </button>
      </header>

      <label>
        {spanish ? 'Producto / plan' : 'Product / plan'}
        <select value={draft.product || ''} onChange={(event) => setField('product', event.target.value || null)}>
          <option value="">{spanish ? 'Sin asignar' : 'Unassigned'}</option>
          {Array.from(new Set([...products.map((row) => row.name), draft.product].filter(Boolean) as string[])).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <label>
        {spanish ? 'Siguiente acción' : 'Next action'}
        <input value={draft.next_action || ''} onChange={(event) => setField('next_action', event.target.value)} />
      </label>
      <div className="pursuit-form pursuit-form-compact">
        <label>
          {spanish ? 'Fecha' : 'Due'}
          <input type="date" value={(draft.next_action_date || '').slice(0, 10)} onChange={(event) => setField('next_action_date', event.target.value || null)} />
        </label>
        <label>
          Owner
          <select
            value={draft.next_action_owner_id || ''}
            onChange={(event) => setField('next_action_owner_id', event.target.value ? Number(event.target.value) : null)}
          >
            <option value="">{spanish ? 'Sin asignar' : 'Unassigned'}</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {spanish ? 'Monto' : 'Amount'}
          <input type="number" min={0} value={draft.amount || 0} onChange={(event) => setField('amount', Number(event.target.value))} />
        </label>
      </div>

      <label>
        {spanish ? 'Dolor (palabras del comprador)' : 'Pain (buyer words)'}
        <textarea value={draft.pain || ''} onChange={(event) => setField('pain', event.target.value)} rows={2} />
      </label>
      <label>
        {spanish ? 'Impacto' : 'Impact'}
        <textarea value={draft.impact || ''} onChange={(event) => setField('impact', event.target.value)} rows={2} />
      </label>
      <label>
        {spanish ? 'Urgencia' : 'Urgency'}
        <textarea value={draft.urgency || ''} onChange={(event) => setField('urgency', event.target.value)} rows={2} />
      </label>

      <div className="pursuit-scores">
        <div>
          <span>Scorecard</span>
          <strong>{opp.scorecard_total ?? 0}/20</strong>
          <small>{spanish ? '16 para salir de Lead' : '16 to leave Lead'}</small>
        </div>
        <div>
          <span>
            MEDDPICC
            <CrmTermHelp term="meddpicc" spanish={spanish} />
          </span>
          <strong>{opp.meddpicc_total ?? 0}/16</strong>
          <small>
            EB {opp.med_economic_buyer ?? 0} · Champ {opp.med_champion ?? 0} · Paper {opp.med_paper_process ?? 0}
          </small>
        </div>
      </div>

      <p className="dashboard-panel-subtitle">{spanish ? 'Scorecard 0–5' : 'Scorecard 0–5'}</p>
      <div className="pursuit-med-grid">
        {SCORE_FIELDS.map(([key, en, es]) => (
          <label key={key}>
            {spanish ? es : en}
            <input
              type="range"
              min={0}
              max={5}
              value={Number(draft[key] || 0)}
              onChange={(event) => setField(key, Number(event.target.value))}
            />
            <span>{Number(draft[key] || 0)}</span>
          </label>
        ))}
      </div>

      <p className="dashboard-panel-subtitle">{spanish ? 'MEDDPICC 0–2 (compuerta de etapa)' : 'MEDDPICC 0–2 (stage gates)'}</p>
      <div className="pursuit-med-grid">
        {MED_FIELDS.map(([key, en, es]) => (
          <label key={key}>
            {spanish ? es : en}
            <input
              type="range"
              min={0}
              max={2}
              value={Number(draft[key] || 0)}
              onChange={(event) => setField(key, Number(event.target.value))}
            />
            <span>{Number(draft[key] || 0)}</span>
          </label>
        ))}
      </div>

      <div className="pursuit-form pursuit-form-compact">
        <label>
          Champion
          <select
            value={draft.champion_contact_id || ''}
            onChange={(event) => setField('champion_contact_id', event.target.value ? Number(event.target.value) : null)}
          >
            <option value="">{spanish ? 'Sin asignar' : 'Unassigned'}</option>
            {contacts.map((row) => (
              <option key={row.id} value={row.id}>
                {row.first_name} {row.last_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {spanish ? 'Comprador económico' : 'Economic buyer'}
          <select
            value={draft.economic_buyer_contact_id || ''}
            onChange={(event) => setField('economic_buyer_contact_id', event.target.value ? Number(event.target.value) : null)}
          >
            <option value="">{spanish ? 'Sin asignar' : 'Unassigned'}</option>
            {contacts.map((row) => (
              <option key={row.id} value={row.id}>
                {row.first_name} {row.last_name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label>
        {spanish ? 'Mover etapa' : 'Move stage'}
        <select value={draft.stage} onChange={(event) => setField('stage', event.target.value)}>
          {STAGES.map((stage) => (
            <option key={stage} value={stage}>
              {stageLabel(stage, spanish)}
            </option>
          ))}
        </select>
      </label>
      {draft.stage === 'closed_lost' ? (
        <label>
          {spanish ? 'Razón de pérdida' : 'Lost reason'}
          <input value={draft.lost_reason || ''} onChange={(event) => setField('lost_reason', event.target.value)} />
        </label>
      ) : null}
      <button type="button" className="dashboard-security-inline-btn" disabled={busy} onClick={submit}>
        {spanish ? 'Guardar trato' : 'Save deal'}
      </button>

      <form
        className="pursuit-form"
        onSubmit={(event) => {
          event.preventDefault();
          const subject = note.trim();
          if (!subject) return;
          onLogActivity(opp.id, subject);
          setNote('');
        }}
      >
        <input value={note} onChange={(event) => setNote(event.target.value)} placeholder={spanish ? 'Registrar nota' : 'Log a note'} />
        <button type="submit" className="dashboard-security-inline-btn" disabled={busy || !note.trim()}>
          {spanish ? 'Registrar' : 'Log'}
        </button>
      </form>
      {activities.length ? (
        <ul className="pursuit-activity-list">
          {activities.slice(0, 8).map((row) => (
            <li key={row.id}>
              <strong>{row.subject}</strong>
              <span>{row.happened_at ? row.happened_at.slice(0, 16).replace('T', ' ') : ''}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="dashboard-panel-subtitle">
        {spanish
          ? `Owner ${users.find((user) => user.id === opp.owner_id)?.name || 'sin asignar'}. Compuertas: siguiente acción, scorecard ≥ 16 + campeón para salir de Lead; comprador económico + campeón para Propuesta; paper process = 2 en Commit ≥ $100k.`
          : `Owner ${users.find((user) => user.id === opp.owner_id)?.name || 'unassigned'}. Gates: next action; scorecard ≥ 16 + champion to leave Lead; economic buyer + champion for Proposal; paper process = 2 on Commit for ≥ $100k.`}
      </p>
    </aside>
  );
};

export default PursuitWorkspace;
